import SearchInput, { SearchInputStyles } from "@gemeco/Overrides/Widgets/Header/SearchInput";
import { GemecoState } from "@gemeco/Store/GemecoState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { SyntheticEvent } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    video = "video",
    desiredHeight = "desiredHeight",
    backupImagePath = "backupImagePath",
}

const mapStateToProps = (state: GemecoState) => {
    return {
        isSecondaryHeader: state.headerPage.isSecondaryHeader,
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.video]: string;
        [fields.desiredHeight]: number;
        [fields.backupImagePath]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface VideoBannerStyles {
    container?: InjectableCss;
    searchInputStyles?: SearchInputStyles;
    abrevSearchInputContainer?: InjectableCss;
    abrevSearchHeadlineTextStyle?: InjectableCss;
    abrevContainerStyles?: InjectableCss;
    abrevSearchInput?: InjectableCss;
    videoContainer?: InjectableCss;
    videoOverlay?: InjectableCss;
}

export const videoBannerStyles: VideoBannerStyles = {
    abrevSearchInputContainer: {
        css: css`
            width: 80%;
            display: flex;
            align-items: center;
        `,
    },

    abrevSearchHeadlineTextStyle: {
        css: css`
            height: auto;
        `,
    },

    abrevContainerStyles: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },

    abrevSearchInput: {
        css: css`
            flex: 0 4 100%;
        `,
    },
    videoContainer: {
        css: css`
            margin-left: -15px;
            margin-right: -15px;
            margin-top: -16px;
            position: relative;
            overflow: hidden;
        `,
    },
    videoOverlay: {
        css: css`
            position: relative !important;
            width: 70%;
            margin: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 95%;
                            margin: auto;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
};

const styles = videoBannerStyles;

const VideoBanner: React.FC<Props> = ({ id, fields, isSecondaryHeader }) => {
    const desiredHeightStyles = `height: ${fields.desiredHeight}px;`;
    const desiredHeightStylesCss = {
        css: css`${desiredHeightStyles}`
    };
    const videoContainerStyles = {
        css: css`
            ${styles.videoContainer?.css || ""}
            ${desiredHeightStyles}
        `,
    };
    let videoOverlayStyles = {
        css: css`
            ${styles.videoOverlay?.css || ""}
            top: -${fields.desiredHeight / 2 + 25}px;
            opacity: 1;
            visibility: visible;
            transition: visibility 1s, opacity 1s;
        `,
    };
    if (isSecondaryHeader) {
        videoOverlayStyles = {
            css: css`
                ${styles.videoOverlay?.css || ""}
                top: -${fields.desiredHeight / 2 + 25}px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                opacity: 0;
                                visibility: hidden;
                                transition: visibility 1s, opacity 1s;
                            `,
                        ],
                        "min",
                    )}
            `,
        };
    }

    return (
        <StyledWrapper {...desiredHeightStylesCss}>
            <StyledWrapper {...videoContainerStyles}>
                <video
                    style={{
                        minWidth: "100%",
                        display: "block",
                        height: `${fields.desiredHeight}px`,
                        objectFit: "fill",
                    }}
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={fields.backupImagePath}
                >
                    <source src={fields.video} type="video/mp4" />
                </video>
                <StyledWrapper className="customSearchBar" {...videoOverlayStyles}>
                    <SearchInput
                        id={id}
                        extendedStyles={styles.searchInputStyles}
                        placeholderText="Search all products..."
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const videoBanner: WidgetModule = {
    component: connect(mapStateToProps)(VideoBanner),
    definition: {
        group: "Basic",
        icon: "Video",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.video,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.desiredHeight,
                displayName: "Desired Banner Height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                tooltip: "Desired height in pixels",
            },
            {
                fieldType: "Translatable",
                name: fields.backupImagePath,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
            },
        ],
    },
};

export default videoBanner;
